// Birth notification (docs/plans/phase2-mortuary-and-notifications.md §1.1 item 9, §5).

import { displayName, type PersonName } from '../person-name.ts';
import { err, ok, type Result } from '../result.ts';

export const BIRTH_NOTIFICATION_STATUSES = ['recorded', 'entered_in_error'] as const;
export type BirthNotificationStatus = (typeof BIRTH_NOTIFICATION_STATUSES)[number];

/** Live births only; stillbirths belong to the maternity slice (plan §1.2). */
export const BIRTH_OUTCOMES = ['live_birth'] as const;
export type BirthOutcome = (typeof BIRTH_OUTCOMES)[number];

/**
 * TODO(clinical-verify) N3: a data-entry bound in whole grams, so a slip of the keyboard
 * (kilograms typed as grams, an extra digit) is caught. It is not a clinical threshold.
 */
export const BIRTH_WEIGHT_GRAMS = { min: 1, max: 9999 } as const;

/** For twins and more: 1 to 9. */
export const BIRTH_ORDER_MAX = 9;

export type BirthTimeErrorCode = 'birth_time.future' | 'birth_time.before_visit';

const MINUTE_MS = 60_000;

/**
 * The time of birth is a clinical event time during the mother's visit, up to now. Times are
 * entered to the minute, so the minute the visit started counts as during it (a baby born
 * at 14:05 on a stay opened at 14:05:30).
 */
export function checkBirthTime(
  bornAt: Date,
  motherVisitStartedAt: Date,
  now: Date,
): Result<Date, BirthTimeErrorCode> {
  if (bornAt.getTime() > now.getTime()) return err('birth_time.future');
  const visitMinute = Math.floor(motherVisitStartedAt.getTime() / MINUTE_MS) * MINUTE_MS;
  if (bornAt.getTime() < visitMinute) return err('birth_time.before_visit');
  return ok(bornAt);
}

export function checkBirthWeight(grams: number): Result<number, 'birth_weight.out_of_bounds'> {
  if (
    !Number.isInteger(grams) ||
    grams < BIRTH_WEIGHT_GRAMS.min ||
    grams > BIRTH_WEIGHT_GRAMS.max
  ) {
    return err('birth_weight.out_of_bounds');
  }
  return ok(grams);
}

/**
 * The baby's name until the family gives one: "Baby of Amina Otieno", or "Baby 2 of …" for
 * the second of twins. TODO(clinical-verify) N2: the naming convention is provisional.
 */
export function babyName(mother: PersonName, birthOrder: number | null): PersonName {
  return {
    given: birthOrder === null ? 'Baby' : `Baby ${String(birthOrder)}`,
    middle: null,
    family: `of ${displayName(mother)}`,
  };
}
